import 'extensions/quick-order/components/quick-order-input';
import {defineCustomElement} from 'extensions/ui-utils';
import {html, LitElement, nothing} from 'lit';
import {classMap} from 'lit/directives/class-map.js';
import stylesheet from './quick-order-app.scss?as=stylesheet';

const defaultInputData = {
	errorMessage: '',
	productCode: '',
	productData: {},
	quantity: 1
};

export class QuickOrderApp extends LitElement {

	static get styles () {
		return [stylesheet];
	}

	static get properties () {
		return {
			active: {
				state: true,
				type: String
			},
			fileData: {
				type: Array
			},
			finishedGoodsInputs: {
				state: true,
				type: Object
			},
			sparePartsInputs: {
				state: true,
				type: Object
			}
		};
	}

	get activeInputs () {
		return this[`${this.active}Inputs`];
	}

	get form () {
		return this.renderRoot?.querySelector('form');
	}

	set activeInputs (val) {
		this[`${this.active}Inputs`] = val;
	}

	constructor () {
		super();

		/**
		 * @type {array}
		 */
		this.sparePartsInputs = [structuredClone(defaultInputData)];

		/**
		 * @type {array}
		 */
		this.finishedGoodsInputs = [structuredClone(defaultInputData)];

		/**
		 * @type {string}
		 */
		this.active = window.mivaJS?.Customer_Permissions?.User_Type === 'BOTH' || window.mivaJS?.Customer_Permissions?.User_Type === 'FINISHED_GOODS' ? 'finishedGoods' : 'spareParts';

		/**
		 * @type {string}
		 */
		this.fileName = '';

		/**
		 * @type {object}
		 */
		this.fileData = {};

		/**
		 * @type {boolean}
		 */
		this.formValid = false;

		this.addEventListener('stateChange', this.handleStateChange);
	}

	isValid () {
		this.formValid = false;

		const values = this.activeInputs.map(value => {
			return value.productCode;
		});

		const removeBlanks = values.filter(value => value);

		if (removeBlanks.length > 0) {
			this.formValid = true;
		}
	}

	handleStateChange (e) {
		const productData = e.detail;
		this.activeInputs[productData.index] = structuredClone(productData);
		this.isValid();
		this.requestUpdate();
	}

	renderToggleSpareParts () {
		if (window.mivaJS?.Customer_Permissions?.User_Type === 'BOTH' || window.mivaJS?.Customer_Permissions?.User_Type === 'SPARE_PARTS') {
			const classes = {
				active: this.active === 'spareParts',
				't-quick-order-toggle': true
			};

			return html`<button class="${classMap(classes)}" @click="${(e) => this.handleToggle(e, 'spareParts')}">Spare Parts</button>`;
		}

		return nothing;
	}

	renderToggleFinishedGoods () {
		if (window.mivaJS?.Customer_Permissions?.User_Type === 'BOTH' || window.mivaJS?.Customer_Permissions?.User_Type === 'FINISHED_GOODS') {
			const classes = {
				active: this.active === 'finishedGoods',
				't-quick-order-toggle': true
			};

			return html`<button class="${classMap(classes)}" @click="${(e) => this.handleToggle(e, 'finishedGoods')}">Products</button>`;
		}

		return nothing;
	}

	render () {
		return html`
			<form id="js-quick-order-form-wrap" class="t-quick-order-form" action="${window.mivaJS.Basket_Url}" method="POST" @submit="${this.handleSubmit}">
			<input type="hidden" name="Action" value="ADPM" />
				<div class="t-quick-order-toggles t-quick-order-wrap">
					${this.renderToggleSpareParts()}
					${this.renderToggleFinishedGoods()}
				</div>
				<div class="t-quick-order-wrap t-quick-order-content">
					<p class="t-quick-order-template">
						Enter each ${this.active === 'spareParts' ? 'part' : 'model'} number or upload a CSV file of your order below. <a href="/downloads/fujitsu_quickorder_template.csv">Download the CSV Template.</a>
					</p>
					<div id="js-quick-order-elements" class="t-quick-order-form__elements">
						<div class="t-quick-order-titles">
							<div class="t-quick-order-title t-quick-order-title__left">
								${this.active === 'spareParts' ? 'Part #' : 'Model #'}
							</div>
							<div class="t-quick-order-title t-quick-order-title__right">
								Qty
							</div>
						</div>
						<div class="t-quick-order-fields u-font-secondary">
							${this.renderActiveInputs()}
						</div>
					</div>
					<div class="t-quick-order-form__footer">
						<div class="u-text-center t-quick-order-form__add">
							<button @click="${this.addNewInput}" class="js-quick-order-add-row u-font-secondary t-quick-order-form__add-cta u-text-medium u-flex o-layout--align-center" type="button"> <span class="t-quick-order-form__add-icon u-icon-add-circle u-text-normal"></span> Add Another Row </button>
						</div>
						<div class="t-quick-order-form__ctas o-layout o-layout--justify-between">
							<div class="o-layout__item u-text-left u-width-4--m u-width-12">
							<label for="fileinput" class="c-button u-font-small u-cta-style--secondary c-button--full">
								Upload File
							</label>
								<input @change="${this.readFile}" type="file" id="fileinput">
							</div>
							<div class="o-layout__item u-width-4--m u-width-12 u-text-right">
								<button class="c-button u-cta-style--primary js-quick-order-submit t-quick-order-submit c-button--full" type="submit" ?disabled="${!this.formValid}"> Add to Cart <span class="js-quick-order-count"></span> </button>
							</div>
						</div>
					</div>
				</div>
			</form>
		`;
	}

	renderActiveInputs () {
		switch (this.active) {
			default:
			case 'spareParts':
				return this.sparePartsInputs.map((item, index) => html`<quick-order-input product-code="${item.productCode}" quantity="${item.quantity}" .productData="${item.productData}" index="${index}" type="spareParts" error-message="${item.errorMessage}"></quick-order-input>`);
			case 'finishedGoods':
				return this.finishedGoodsInputs.map((item, index) => html`<quick-order-input product-code="${item.productCode}" quantity="${item.quantity}" .productData="${item.productData}" index="${index}" type="finishedGoods" error-message="${item.errorMessage}"></quick-order-input>`);
		}
	}

	addNewInput () {
		this.activeInputs.push(structuredClone(defaultInputData));
		this.requestUpdate();
	}

	readFile (evt) {
		const f = evt.target.files[0];
		let output = [];
		if (f) {
			var r = new FileReader();
			r.onload = (e) => {
				const contents = e.target.result;
				this.fileName = f.name;
				const lines = contents.split('\r\n');
				for (let i = 0; i < lines.length - 1; i++) {
					const line = lines[i].split(',');
					const prodSku = line[0];
					const prodQty = line[1];
					if (Number.isInteger(parseInt(prodQty, 10))) {
						output.push({
							qty: prodQty,
							sku: prodSku
						});
					}
				}
				this.fileData = output;
				this.handleFileData();
			};
			r.readAsText(f);
		} else {
			this.fileName = 'Error: Issue loading file.';
		}
	}

	handleFileData () {
		let rows = [];
		for (const row of this.fileData) {
			rows.push({
				errorMessage: '',
				productCode: row.sku,
				productData: {},
				quantity: row.qty
			});
		}
		this.activeInputs = rows;
		this.requestUpdate();
	}

	handleToggle (e, type) {
		e.preventDefault();

		this.active = type;

		this.requestUpdate();
	}

	handleSubmit () {
		const gtmProducts = [];
		for (const product of this.activeInputs) {
			if (product.quantity === 0 || product.errorMessage !== '') {
				continue;
			}

			gtmProducts.push({
				/* eslint-disable camelcase */
				item_id: product.productData?.product?.code,
				item_list_id: 'quick-order',
				item_list_name: 'Quick Order',
				item_name: product.productData?.product?.name,
				/* eslint-enable camelcase */
				price: product.productData?.product?.price,
				quantity: product.quantity
			});
		}

		if (gtmProducts.length) {
			window.dataLayer.push({
				ecommerce: null
			});

			window.dataLayer.push({
				ecommerce: {
					add: {
						products: gtmProducts
					},
					currencyCode: window.mivaJS?.Currency_Code
				},
				event: 'addToCart'
			});
		}
	}
};

defineCustomElement('quick-order-app', QuickOrderApp);